import { useState } from "react";
import { message, Modal, Checkbox } from "antd";
import useCartStore from "../../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../../stores/useTransactionStore";

/**
 * Hook for SuperMarket-specific return flow logic:
 * - Return record selection (checkbox table)
 * - Return quantity editing
 * - Return reason form submission (onFinish)
 * - Receiptless / blind return handling
 * - Render helpers for return / exchange amounts
 *
 * @param {Object} deps
 * @param {Object}   deps.cart
 * @param {Function} deps.setCart
 * @param {Object}   deps.tillData
 * @param {Object}   deps.posConfig
 * @param {number}   deps.pricePrecision
 * @param {Array}    deps.dataSource
 * @param {Function} deps.setDataSource
 * @param {Array}    deps.checkedKeys
 * @param {Function} deps.setCheckedKeys
 * @param {Array}    deps.giftCardItems
 * @param {Function} deps.handleSaveReturnCartRecord
 * @param {Function} deps.handleCartTotalClick
 * @param {Function} deps.setReasonPopup
 * @param {Function} deps.setTillLink
 * @param {Function} deps.setFilterDrawer
 * @param {Function} deps.setShowReturnOptions
 * @param {Function} deps.setIsInputFocused
 */
const useSuperMarketReturn = ({
  cart,
  setCart,
  tillData,
  posConfig,
  pricePrecision,
  dataSource,
  setDataSource,
  checkedKeys,
  setCheckedKeys,
  giftCardItems,
  handleSaveReturnCartRecord,
  handleCartTotalClick,
  setReasonPopup,
  setTillLink,
  setFilterDrawer,
  setShowReturnOptions,
  setIsBlindReceiptPending,
  setIsInputFocused,
}) => {
  // ── State ────────────────────────────────────────────────────────────
  const [isReturn, setIsReturn] = useState(false);
  const [returnFlag, setReturnFlag] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const currenciesList = tillData.tillAccess.csBunit.currencies;
  const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";

  // ── Selection handlers ───────────────────────────────────────────────

  const onSelectAllChange = (e) => {
    if (e.target.checked) {
      const allKeys = dataSource.map((record) => {
        let giftCardItem = giftCardItems.filter((giftCard) => giftCard.mProductId === record.product.mProductId);
        if (record.returnQty < record.qty && giftCardItem.length === 0) return record.key;
      });
      setCheckedKeys(allKeys);
      setSelectedRecords(dataSource);
    } else {
      setCheckedKeys([]);
      setSelectedRecords([]);
    }
  };

  const onSelect = (record) => {
    const newCheckedKeys = [...checkedKeys];
    const recordKey = record.key;
    let giftCardItem = giftCardItems.filter((giftCard) => giftCard.mProductId === record.product.mProductId);
    if (newCheckedKeys.includes(recordKey)) {
      const index = newCheckedKeys.indexOf(recordKey);
      newCheckedKeys.splice(index, 1);

      const newSelectedRecords = selectedRecords.filter((selectedRecord) => selectedRecord.key !== recordKey);
      setSelectedRecords(newSelectedRecords);
    } else {
      const index = dataSource.findIndex((record) => record.key === recordKey);
      if (dataSource[index].returnQty < dataSource[index].qty && giftCardItem.length === 0) {
        newCheckedKeys.push(recordKey);
      }

      const newSelectedRecords = [...selectedRecords, record];
      setSelectedRecords(newSelectedRecords);
    }

    setCheckedKeys(newCheckedKeys);
  };

  const rowSelection = {
    selectedRowKeys: checkedKeys,
    onChange: (selectedKeys) => {
      setCheckedKeys(selectedKeys);
    },
  };

  // ── Quantity editing ─────────────────────────────────────────────────

  const inputReturnQty = (record, e) => {
    dataSource.map((item) => {
      if (item.key === record.key) {
        item.qtyToReturn = e.target.value;
      }
    });
    setDataSource(dataSource);
  };

  // ── Return columns definition ────────────────────────────────────────

  const columns1 = [
    {
      title: <Checkbox onChange={onSelectAllChange} checked={checkedKeys.length === dataSource.length} />,
      width: "7%",
      dataIndex: "selection",
      render: (_, record) => <Checkbox checked={checkedKeys.includes(record.key)} onChange={() => onSelect(record)} />,
    },
    {
      title: "Description",
      dataIndex: "product",
      width: "25%",
      render: (_, record) => {
        return record.product.name;
      },
    },
    {
      title: "Unit Price",
      dataIndex: "grossstd",
      width: "15%",
      render: (_, record) => {
        return parseFloat(record.grossstd).toFixed(pricePrecision);
      },
    },
    {
      title: "Qty Ordered",
      dataIndex: "qty",
      width: "15%",
    },
    {
      title: "Remaining Qty",
      dataIndex: "remainingQty",
      width: "15%",
    },
    {
      title: "Qty to Return",
      dataIndex: "qtyToReturn",
      width: "15%",
    },
  ];

  // ── Return reason form ───────────────────────────────────────────────

  const onFinish = async (values) => {
    let updatedCart;
    const returnSummary = {
      returnReferenceNo: values.referenceNo,
      returnRemarks: values.remarks,
    };
    const activeCart = isExchange ? (useCartStore.getState().exchangeCart || {}) : cart;
    updatedCart = {
      ...activeCart,
      returnSummary,
      returnReasonId: values.reason,
    };
    if (isExchange) {
      useCartStore.getState().setExchangeCart(updatedCart);
    } else {
      setCart(updatedCart);
    }
    await handleCartTotalClick();
    setReasonPopup(false);
  };

  // ── Receiptless / blind return handler ───────────────────────────────

  const handleBlindReturn = () => {
    const posConfigLS = usePOSConfigStore.getState().posConfig;
    const walletEnabled = posConfigLS?.enableReceiptlessReturn2 === "Y";

    // Wallet flow
    if (walletEnabled) {
      if (cart.isReturn === true) return;

      if (cart.payments.length > 0) {
        message.error("Payments already added. Cannot process return.");
        return;
      }

      setTillLink(false);
      if (posConfig?.allowReturnRefund === "Y" || posConfig?.allowReturnCreditNote === "Y" || posConfig?.allowReturnExchange === "Y") {
        // BUG-1213: Defer cart.isReturn until user confirms in ReturnOptionsModal.
        // Setting isReturn prematurely causes stale state after browser refresh.
        setIsBlindReceiptPending(true);
        setShowReturnOptions(true);
      } else {
        // No return options — apply immediately since user has no choice to cancel
        const updatedCart = {
          ...cart,
          isReturn: true,
          isBlindReceipt: true,
        };
        setCart(updatedCart);
        useTransactionStore.getState().setSelectedReturnType("REFUND");
        setFilterDrawer(true);
      }
      return;
    }

    // Classic receiptless return flow
    if (cart.isReturn !== true && cart.items.length > 0 && cart.payments.length === 0) {
      Modal.confirm({
        title: "You are about to process a return without a receipt. Would you like to proceed?",
        icon: null,
        cancelText: "No",
        okText: "Yes",
        autoFocusButton: null,
        okButtonProps: { style: { display: "inline-block" } },
        onOk: async () => {
          setTillLink(false);
          let updatedCart = { ...cart };

          updatedCart.total = -updatedCart.total;
          updatedCart.tax = -updatedCart.tax;
          updatedCart.isReturn = true;
          updatedCart.isBlindReceipt = true;
          updatedCart.totalQty = -updatedCart.totalQty;

          updatedCart.items = updatedCart.items.map((item) => {
            let grossUnit = (Math.abs(item.sunitprice) - Math.abs(item.discount)) / Math.abs(item.weight);
            let newItem = { ...item };

            newItem.nettotal = -item.nettotal;
            newItem.weight = -item.weight;
            newItem.isReturn = true;
            newItem.sunitprice = -item.sunitprice;
            newItem.netList = -item.nettotal;
            newItem.linetax = Math.abs(newItem.nettotal) > 0 ? -parseFloat(item.taxAmount.toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision)) : 0;
            newItem.linenet =
              Math.abs(newItem.nettotal) > 0
                ? -parseFloat((item.nettotal - item.taxAmount).toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision))
                : 0;
            newItem.linegross = Math.abs(newItem.nettotal) > 0 ? parseFloat(item.nettotal.toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision)) : 0;
            newItem.netStd = -item.netStd;
            newItem.listPrice = Math.abs(newItem.nettotal) > 0 ? -item.listPrice : 0;
            newItem.grossunit =
              Math.abs(newItem.nettotal) > 0
                ? item.isReturn
                  ? -parseFloat(grossUnit.toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision))
                  : parseFloat(grossUnit.toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision))
                : 0;
            newItem.taxAmount = -item.taxAmount;
            return newItem;
          });

          await setCart(updatedCart);
          if (posConfig?.showReason === "Y" || posConfig?.showReferenceNo === "Y" || posConfig?.showRemarks === "Y") {
            setReasonPopup(true);
          } else {
            await handleCartTotalClick();
          }
        },
      });
    }
  };

  // ── Computed flags ───────────────────────────────────────────────────

  const returnEnable = posConfig?.enableReceiptlessReturn2 === "Y";
  const isReturnDisabled = returnEnable
    ? cart.isReturn || cart.items.length > 0
    : !(cart.items.length > 0 && !cart.isReturn);
  const shouldShowReturnButton = returnEnable
    ? true
    : posConfig?.enableReceiptlessReturn === "Y";

  // ── Render helpers ───────────────────────────────────────────────────

  const renderReturnAmount = () => {
    return (
      <span
        style={{
          color: "#D32F2F",
          fontWeight: 500,
          fontSize: "0.8em",
        }}
      >
        {currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
        {parseFloat(useCartStore.getState().cart?.total ?? cart.total).toFixed(2)}
        {currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
      </span>
    );
  };

  const renderExchangeAmount = () => {
    return (
      <span
        style={{
          color: "#2F8F46",
          fontWeight: 500,
          fontSize: "0.7em",
        }}
      >
        {currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""}
        {parseFloat(useCartStore.getState().exchangeCart?.total ?? 0).toFixed(2)}
        {currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}
      </span>
    );
  };

  const renderTotalAmountText = () => {
    const returnCart = useCartStore.getState().cart;
    const exchangeCart = useCartStore.getState().exchangeCart || {};

    const returnTotal = Math.abs(parseFloat(returnCart?.total || 0));
    const exchangeTotal = Math.abs(parseFloat(exchangeCart?.total || 0));

    const isExchangeMode = useTransactionStore.getState().selectedReturnType === "EXCHANGE";

    const isRefund = isExchangeMode && returnTotal > exchangeTotal;

    return (
      <p
        style={{
          padding: "0.7vw 0 0 1vw",
          color: "#fff",
          fontSize: "1em",
          fontWeight: "500",
          height: "4.5vh",
        }}
      >
        {isRefund ? "Refund Amount" : "Total Amount To Pay"}
      </p>
    );
  };

  // ── Return ───────────────────────────────────────────────────────────
  return {
    // state
    isReturn,
    setIsReturn,
    returnFlag,
    setReturnFlag,
    selectedRecords,
    setSelectedRecords,
    expandedRowKeys,
    setExpandedRowKeys,

    // selection
    onSelectAllChange,
    onSelect,
    rowSelection,

    // editing
    inputReturnQty,
    columns1,

    // return reason
    onFinish,

    // blind return
    handleBlindReturn,

    // computed flags
    returnEnable,
    isReturnDisabled,
    shouldShowReturnButton,

    // render helpers
    renderReturnAmount,
    renderExchangeAmount,
    renderTotalAmountText,
  };
};

export default useSuperMarketReturn;
